import { useCallback, useState, type MutableRefObject } from "react";

import type { RfxQuoteTable } from "../../../types/quote.type";
import type { ScheduleOfRatesHandle } from "newui/features/Rfx/components/ScheduleOfRates";
import type { SupplierRowsSectionHandle } from "../SupplierRowsSection";
import { getClientRows, type QuoteFieldValues } from "../BidderQuoteTab.helpers";

export interface UseQuoteValidationResult {
	quoteValidationErrors: Set<number>;
	scheduleValidationError: boolean;
	clientRowErrors: Record<number, { cells?: Record<number, boolean> }>;
	validate: () => boolean;
	clearCellError: (itemId: number, field: string, value: number | string) => void;
}

/**
 * Owns validation state for BidderQuoteTab: per-quote failure set, schedule
 * failure flag, and per-row response-cell error map. Exposes a `validate()`
 * imperative trigger plus a `clearCellError` helper used by the change handler
 * to reactively dismiss errors as the user types.
 */
export const useQuoteValidation = (
	listQuotes: RfxQuoteTable[] | undefined,
	values: QuoteFieldValues | undefined,
	isClientView: boolean,
	supplierRowRefs: MutableRefObject<Record<number, SupplierRowsSectionHandle | null>>,
	scheduleRef: MutableRefObject<ScheduleOfRatesHandle | null>,
): UseQuoteValidationResult => {
	const [quoteValidationErrors, setQuoteValidationErrors] = useState<Set<number>>(
		new Set(),
	);
	const [scheduleValidationError, setScheduleValidationError] = useState(false);
	const [clientRowErrors, setClientRowErrors] = useState<
		Record<number, { cells?: Record<number, boolean> }>
	>({});

	const validate = useCallback((): boolean => {
		const failedQuoteIds = new Set<number>();

		for (const [quoteId, handle] of Object.entries(supplierRowRefs.current)) {
			if (handle && !handle.validate()) {
				failedQuoteIds.add(Number(quoteId));
			}
		}

		const cellErrors: Record<number, { cells?: Record<number, boolean> }> = {};
		if (!isClientView) {
			listQuotes?.forEach((quote) => {
				if (!quote.columns?.length) {
					return;
				}
				const clientRows = getClientRows(quote.rows);
				const supplierEditableCols = quote.columns.filter(
					(c) =>
						c.isSupplierEditable &&
						(c.columnType === "response_value" ||
							c.columnType === "response_quantity" ||
							c.columnType === "response_number"),
				);
				for (const row of clientRows) {
					const key = `quote-${row.id}`;
					const rowValues = values?.[key] ?? {};
					const emptyCells: Record<number, boolean> = {};
					for (const col of supplierEditableCols) {
						let fieldKey: string;
						if (col.columnType === "response_value") {
							fieldKey = `rate_${col.id}`;
						} else if (col.columnType === "response_quantity") {
							fieldKey = "quantity";
						} else {
							fieldKey = `number_${col.id}`;
						}
						const val = rowValues[fieldKey];
						if (val === undefined || val === null || val === "") {
							emptyCells[col.id] = true;
						}
					}
					if (Object.keys(emptyCells).length > 0) {
						cellErrors[row.id] = { cells: emptyCells };
						failedQuoteIds.add(quote.id);
					}
				}
			});
		}
		setClientRowErrors(cellErrors);

		const scheduleInvalid =
			scheduleRef.current && !scheduleRef.current.validate();

		setQuoteValidationErrors(failedQuoteIds);
		setScheduleValidationError(!!scheduleInvalid);

		return failedQuoteIds.size === 0 && !scheduleInvalid;
	}, [isClientView, listQuotes, values, supplierRowRefs, scheduleRef]);

	const clearCellError = useCallback(
		(itemId: number, field: string, value: number | string) => {
			if (value === "" || value === 0) {
				return;
			}
			const match = field.match(/^(?:rate|text|number|qty)_(\d+)$/);
			if (!match) {
				return;
			}
			const colId = Number(match[1]);
			setClientRowErrors((prev) => {
				const itemErrors = prev[itemId];
				if (!itemErrors?.cells?.[colId]) {
					return prev;
				}
				const { [colId]: _removed, ...remainingCells } = itemErrors.cells;
				if (Object.keys(remainingCells).length === 0) {
					const { [itemId]: _itemRemoved, ...rest } = prev;
					return rest;
				}
				return { ...prev, [itemId]: { cells: remainingCells } };
			});
		},
		[],
	);

	return {
		quoteValidationErrors,
		scheduleValidationError,
		clientRowErrors,
		validate,
		clearCellError,
	};
};
